import React, { useEffect, useState } from "react";
import { useFormContext, useWatch, useFormState } from "react-hook-form";
import DateInput from "@shared/components/DateInput";
import { useProject } from "@utils/useProject";
import MultiSelectDropdown from "@pages/Program/components/MultiSelectDropdown";
import { ProjectDropdownValue } from "@shared/interfaces/ProgramFiles";

const GoalForm: React.FC<{
  dataLoading?: boolean;
  startDate: string;
  endDate: string;
}> = ({ dataLoading, startDate, endDate }) => {
  const [projectTouched, setProjectTouched] = useState(false);
  const [allProjects, setAllProjects] = useState<ProjectDropdownValue[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<
    ProjectDropdownValue[]
  >([]);

  const { register, setValue, control, setError, clearErrors, watch } =
    useFormContext();
  const { errors } = useFormState({ control });

  const values = watch();
  const projects = useWatch({ name: "projects", control });

  const { getProjectsDropdown } = useProject();

  const fetchProjectDropdown = async (
    query: string
  ): Promise<ProjectDropdownValue[]> => {
    const data = await getProjectsDropdown(query);
    setAllProjects(data);
    return data;
  };

  useEffect(() => {
    register("project_ids");
  }, [register]);

  useEffect(() => {
    register("projects", {
      validate: (value) =>
        value?.length > 0 || "Please select at least one project",
    });
  }, [register]);

  useEffect(() => {
    if (
      Array.isArray(projects) &&
      projects.length > 0 &&
      selectedProjects.length === 0
    ) {
      setSelectedProjects(projects);
    }
  }, [projects]);

  useEffect(() => {
    setValue("projects", selectedProjects, { shouldValidate: true });
    setValue(
      "project_ids",
      selectedProjects.map((project) => project.id)
    );
  }, [selectedProjects, setValue]);

  useEffect(() => {
    if (projectTouched) {
      if (projects?.length > 0) {
        clearErrors("projects");
      } else {
        setError("projects", {
          type: "manual",
          message: "Please select at least one project",
        });
      }
    }
  }, [projects, projectTouched, setError, clearErrors]);

  return (
    <div className="space-y-5">
      {/* Goal Name */}
      <div>
        <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
          Goal name <span className="text-red-600">*</span>
        </label>
        <input
          {...register("name", { required: "Goal name is required" })}
          className="w-full border border-gray-300 rounded-md p-3 text-sm 2xl:text-base focus:outline-none focus:border-blue-500"
          placeholder="Add a name"
        />
        {errors.name && (
          <p className="text-red-500 text-xs 2xl:text-sm pt-1">
            {String(errors.name.message)}
          </p>
        )}
      </div>

      {/* Goal Description */}
      <div>
        <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
          Goal description <span className="text-red-600">*</span>
        </label>
        <textarea
          {...register("description", {
            required: "Goal description is required",
          })}
          className="w-full border border-gray-300 rounded-md p-3 text-sm 2xl:text-base focus:outline-none focus:border-blue-500"
          placeholder="Type anything..."
        />
        {errors.description && (
          <p className="text-red-500 text-xs 2xl:text-sm pt-1">
            {String(errors.description.message)}
          </p>
        )}
      </div>

      {/* Dates */}
      <div className="flex gap-4">
        <div className="w-1/2">
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Start date <span className="text-red-600">*</span>
          </label>
          <DateInput
            {...register("start_date", {
              required: "Start Date is required",
              validate: (value) =>
                !values.end_date || new Date(value) <= new Date(values.end_date)
                  ? true
                  : "Start Date should be less than or equal to End Date",
            })}
            value={values.start_date}
            minDate={startDate}
            maxDate={endDate}
          />
          {errors.start_date && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.start_date.message)}
            </p>
          )}
        </div>

        <div className="w-1/2">
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            End date <span className="text-red-600">*</span>
          </label>
          <DateInput
            {...register("end_date", {
              required: "End Date is required",
              validate: (value) =>
                !values.start_date ||
                new Date(value) >= new Date(values.start_date)
                  ? true
                  : "End Date should be greater than or equal to Start Date",
            })}
            value={values.end_date}
            minDate={startDate}
            maxDate={endDate}
          />
          {errors.end_date && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.end_date.message)}
            </p>
          )}
        </div>
      </div>

      {/* Project Dropdown */}
      <div>
        <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
          Projects <span className="text-red-600">*</span>
        </label>

        <MultiSelectDropdown
          selected={selectedProjects}
          onChange={(value) => {
            setProjectTouched(true);
            setSelectedProjects(value);
          }}
          fetchOptions={fetchProjectDropdown}
          labelKey="name"
          valueKey="id"
          placeholder="Search Projects..."
          triggerDirty={() => setProjectTouched(true)}
          queryLengthCheck={1}
          loading={dataLoading}
        />

        {errors.projects && (
          <p className="text-red-500 text-xs 2xl:text-sm pt-1">
            {String(errors.projects.message)}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Select project(s) that will impact this goal
        </p>
      </div>
    </div>
  );
};

export default GoalForm;
